"use client"

import { memo, useState } from "react"
import Link from "next/link"
import { useTranslation } from "@/components/language-provider"
import LanguageToggle from "@/components/language-toggle"
import { SITE_CONFIG } from "@/lib/constants"
import { cn } from "@/lib/utils"
import { Menu, X } from "lucide-react"

const Header = memo(function Header() {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const navLinks = [
    { label: t("nav.courses"), href: "/egitimler/" },
    { label: t("nav.instructors"), href: "/egitmenler/" },
  ]

  return (
    <header className="sticky top-0 z-50 w-full" data-testid="beauty-academy-header">
      <div className="container mx-auto px-4 py-4">
        <nav className="glass-nav rounded-2xl px-5 py-3 flex items-center justify-between">
          <Link href="/" className="flex items-center" data-testid="beauty-academy-header-logo-link">
            <img src="/images/glossgo-logo.svg" alt="glossgo" className="h-9 w-auto" />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                data-testid={`beauty-academy-header-nav-row-${link.href}`}
                className="text-gray-700 font-medium hover:text-purple-600 transition-colors duration-150"
              >
                {link.label}
              </Link>
            ))}
            <a
              href={SITE_CONFIG.mainSite}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="beauty-academy-header-glossgo-main-link"
              className="text-gray-700 font-medium hover:text-purple-600 transition-colors duration-150"
            >
              {t("nav.glossgoMain")}
            </a>
          </div>

          <div className="flex items-center gap-2.5">
            <LanguageToggle />
            <Link
              href="/egitmen-ol/"
              data-testid="beauty-academy-header-become-instructor-link"
              className="hidden sm:inline-flex items-center rounded-full bg-[#ee6dfd] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#d376fb] hover:shadow-md"
            >
              {t("nav.becomeInstructor")}
            </Link>
            <button
              onClick={() => setMobileOpen((prev) => !prev)}
              data-testid="beauty-academy-header-mobile-menu-toggle"
              className="md:hidden p-2 rounded-full text-gray-700 hover:text-purple-600 hover:bg-purple-50 transition-colors"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </nav>

        {/* Mobile drawer */}
        {mobileOpen && (
          <div className="md:hidden mt-2 glass-nav rounded-2xl px-5 py-4" data-testid="beauty-academy-header-mobile-drawer">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  data-testid={`beauty-academy-header-mobile-nav-row-${link.href}`}
                  className="py-2.5 px-3 rounded-xl text-gray-700 font-medium hover:text-purple-600 hover:bg-purple-50 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href={SITE_CONFIG.mainSite}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="beauty-academy-header-mobile-glossgo-main-link"
                className="py-2.5 px-3 rounded-xl text-gray-700 font-medium hover:text-purple-600 hover:bg-purple-50 transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("nav.glossgoMain")}
              </a>
              <Link
                href="/egitmen-ol/"
                data-testid="beauty-academy-header-mobile-become-instructor-link"
                className="mt-2 inline-flex items-center justify-center rounded-full bg-[#ee6dfd] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#d376fb] transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                {t("nav.becomeInstructor")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  )
})

export default Header
